<template>
    <div>
        <form
            ref="formRef"
            :action="action"
            method="post"
            @submit="handleSubmit"
            v-if="rulesList.length > 0 || true"
        >
            <input type="hidden" name="_token" :value="csrfToken" />

            <div
                v-for="(rule, index) in rulesList"
                :key="rule.id || index"
                class="q-mb-md bg-white shadow-2 q-pa-md rounded-borders"
            >
                <div class="row q-col-gutter-md">
                    <div class="col-12 col-sm-6">
                        <label class="text-caption text-grey-8">{{
                            labels.type
                        }}</label>
                        <q-select
                            v-model="rule.rule_type"
                            :options="typeOptions"
                            dense
                            outlined
                            emit-value
                            map-options
                            :name="`rules[${index}][rule_type]`"
                            required
                        />
                    </div>

                    <div class="col-12 col-sm-6">
                        <label class="text-caption text-grey-8">{{
                            labels.value
                        }}</label>
                        <q-input
                            v-model.number="rule.value"
                            type="number"
                            dense
                            outlined
                            :name="`rules[${index}][value]`"
                            required
                            min="0"
                            :suffix="
                                rule.rule_type &&
                                rule.rule_type.includes('percentage')
                                    ? '%'
                                    : ''
                            "
                        />
                    </div>

                    <div class="col-12 col-sm-6">
                        <label class="text-caption text-grey-8">{{
                            labels.severity
                        }}</label>
                        <q-select
                            v-model="rule.severity"
                            :options="severityOptions"
                            dense
                            outlined
                            emit-value
                            map-options
                            :name="`rules[${index}][severity]`"
                            required
                        />
                    </div>

                    <div class="col-12 col-sm-6">
                        <label class="text-caption text-grey-8">{{
                            labels.visibility
                        }}</label>
                        <q-select
                            v-model="rule.visibility"
                            :options="visibilityOptions"
                            dense
                            outlined
                            emit-value
                            map-options
                            :name="`rules[${index}][visibility]`"
                            required
                        />
                    </div>

                    <div class="col-12">
                        <label class="text-caption text-grey-8">{{
                            labels.notifications
                        }}</label>
                        <div class="text-caption text-grey-6 q-mb-xs">
                            {{ labels.notifications_info }}
                        </div>
                        <div class="row q-gutter-sm">
                            <q-checkbox
                                v-model="rule.notify_24h_before"
                                :label="labels.notify_24h_before"
                                :name="`rules[${index}][notify_24h_before]`"
                                :true-value="1"
                                :false-value="0"
                                @update:model-value="
                                    (val) => {
                                        if (
                                            val &&
                                            !rule.notify_team_leader &&
                                            !rule.notify_default_recipient
                                        )
                                            rule.notify_team_leader = 1;
                                    }
                                "
                            />
                            <q-checkbox
                                v-model="rule.notify_team_leader"
                                :label="labels.notify_team_leader"
                                :name="`rules[${index}][notify_team_leader]`"
                                :true-value="1"
                                :false-value="0"
                                :disable="!rule.notify_24h_before"
                                @update:model-value="
                                    (val) => {
                                        if (
                                            !val &&
                                            !rule.notify_default_recipient
                                        )
                                            rule.notify_24h_before = 0;
                                    }
                                "
                            />
                            <q-checkbox
                                v-model="rule.notify_default_recipient"
                                :label="labels.notify_default_recipient"
                                :name="`rules[${index}][notify_default_recipient]`"
                                :true-value="1"
                                :false-value="0"
                                :disable="!rule.notify_24h_before"
                                @update:model-value="
                                    (val) => {
                                        if (!val && !rule.notify_team_leader)
                                            rule.notify_24h_before = 0;
                                    }
                                "
                            />
                        </div>
                    </div>

                    <div class="col-12 text-right">
                        <q-btn
                            color="negative"
                            size="sm"
                            icon="delete"
                            :label="labels.delete"
                            @click="removeRule(index)"
                        />
                    </div>
                </div>
            </div>

            <div class="row q-mb-md">
                <div class="col-12">
                    <q-btn
                        color="positive"
                        icon="add"
                        :label="labels.add_rule"
                        @click="addRule"
                    />
                </div>
            </div>

            <div class="row q-col-gutter-md q-mt-md">
                <div class="col-12">
                    <div
                        v-if="errorMessage"
                        class="q-mb-md text-negative text-weight-bold"
                    >
                        {{ errorMessage }}
                    </div>
                    <q-btn
                        type="submit"
                        color="primary"
                        :label="labels.save"
                        class="full-width"
                    />
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import { ref, onMounted, watch } from "vue";
import { useQuasar } from "quasar";

export default {
    name: "CampaignRulesEditor",
    props: {
        initialRules: {
            type: Array,
            default: () => [],
        },
        action: {
            type: String,
            required: true,
        },
        csrfToken: {
            type: String,
            required: true,
        },
        labels: {
            type: Object,
            required: true,
        },
        typeOptions: {
            type: Array,
            required: true,
        },
        severityOptions: {
            type: Array,
            required: true,
        },
        visibilityOptions: {
            type: Array,
            required: true,
        },
    },
    setup(props) {
        const $q = useQuasar();
        const rulesList = ref([]);
        const formRef = ref(null);
        const errorMessage = ref("");

        onMounted(() => {
            rulesList.value = props.initialRules.map((rule) => ({
                ...rule,
                notify_24h_before: rule.notify_24h_before ? 1 : 0,
                notify_team_leader: rule.notify_team_leader ? 1 : 0,
                notify_default_recipient: rule.notify_default_recipient ? 1 : 0,
            }));
        });

        const addRule = () => {
            rulesList.value.push({
                id: "new_" + Date.now(),
                rule_type: "",
                value: "",
                severity: "warning",
                visibility: "private",
                notify_24h_before: 0,
                notify_team_leader: 0,
                notify_default_recipient: 0,
            });
        };

        watch(
            () => rulesList.value,
            (newList) => {
                newList.forEach((rule) => {
                    if (!rule.notify_24h_before) {
                        rule.notify_team_leader = 0;
                        rule.notify_default_recipient = 0;
                    }
                });
            },
            { deep: true },
        );

        const removeRule = (index) => {
            rulesList.value.splice(index, 1);
        };

        const handleSubmit = (event) => {
            errorMessage.value = "";
            const invalidRule = rulesList.value.find(
                (rule) =>
                    rule.notify_24h_before &&
                    !rule.notify_team_leader &&
                    !rule.notify_default_recipient,
            );

            if (invalidRule) {
                event.preventDefault();
                errorMessage.value = props.labels.notify_recipient_required;
                $q.notify({
                    type: "negative",
                    message: props.labels.notify_recipient_required,
                });
            }
        };

        return {
            rulesList,
            formRef,
            errorMessage,
            addRule,
            removeRule,
            handleSubmit,
        };
    },
};
</script>
